import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

/**
 * Dialog pentru predarea unui PSA din stoc la mai mulți angajați odată.
 * Ex: 10 perechi de cizme mărimea 42 → repartizate la 3 angajați (3+4+3).
 *
 * Props:
 *  - item: itemul-sursă din stoc (cu quantity, name, psa_size, etc.)
 *  - open: boolean
 *  - onClose: () => void
 *  - onDone: () => void  // refresh după predare
 */
// uid generator pentru chei stabile pe rândurile dialogului
let _rowUidSeq = 0;
const makeRowUid = () => `row-${Date.now().toString(36)}-${(_rowUidSeq += 1)}`;

export default function BulkAssignDialog({ item, open, onClose, onDone }) {
  const [persons, setPersons] = useState([]);
  const [rows, setRows] = useState([{ _uid: makeRowUid(), person_id: "", quantity: 1 }]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const todayIso = new Date().toISOString().slice(0, 10);
  const [operationDate, setOperationDate] = useState(todayIso);

  const totalStock = parseFloat(item?.quantity || 1);

  useEffect(() => {
    if (!open) return;
    setRows([{ _uid: makeRowUid(), person_id: "", quantity: 1 }]);
    setNote("");
    setOperationDate(todayIso);
    api.get("/persons").then((r) => {
      // filtrează doar activi pentru a evita zgomot în dropdown
      const list = (r.data || []).filter((p) => p.active !== false);
      list.sort((a, b) => {
        const na = `${a.nume || ""} ${a.prenume || ""}`.trim().toLowerCase();
        const nb = `${b.nume || ""} ${b.prenume || ""}`.trim().toLowerCase();
        return na.localeCompare(nb);
      });
      setPersons(list);
    }).catch(() => setPersons([]));
  }, [open, item?.id]);

  const usedIds = useMemo(() => new Set(rows.map((r) => r.person_id).filter(Boolean)), [rows]);
  const totalRequested = useMemo(
    () => rows.reduce((s, r) => s + (parseFloat(r.quantity) || 0), 0),
    [rows],
  );
  const remaining = totalStock - totalRequested;

  const addRow = () => setRows((rs) => [...rs, { _uid: makeRowUid(), person_id: "", quantity: 1 }]);
  const removeRow = (idx) => setRows((rs) => rs.filter((_, i) => i !== idx));
  const updateRow = (idx, patch) => setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const handleSubmit = async () => {
    const valid = rows.filter((r) => r.person_id && parseFloat(r.quantity) > 0);
    if (valid.length === 0) {
      toast.error("Adaugă cel puțin un angajat cu cantitate > 0");
      return;
    }
    if (!operationDate) {
      toast.error("Specifică data predării");
      return;
    }
    if (totalRequested > totalStock) {
      toast.error(`Cantitate totală cerută (${totalRequested}) > stoc disponibil (${totalStock})`);
      return;
    }
    // Duplicate detection
    const seen = new Set();
    for (const r of valid) {
      if (seen.has(r.person_id)) {
        const name = persons.find((p) => p.id === r.person_id);
        toast.error(`Angajat duplicat: ${name ? `${name.nume} ${name.prenume}` : "—"}`);
        return;
      }
      seen.add(r.person_id);
    }

    setBusy(true);
    let okCount = 0;
    const errors = [];
    // Procesare SECVENȚIALĂ — split-assign mută cantități pe parent, nu poate rula în paralel pentru același parent
    for (const r of valid) {
      try {
        await api.post(`/inventory/${item.id}/split-assign`, {
          person_id: r.person_id,
          quantity: parseFloat(r.quantity),
          note,
          operation_date: operationDate,
        });
        okCount += 1;
      } catch (e) {
        const p = persons.find((x) => x.id === r.person_id);
        const who = p ? `${p.nume} ${p.prenume}` : r.person_id;
        errors.push(`${who}: ${e?.response?.data?.detail || "eroare"}`);
      }
    }
    setBusy(false);
    if (okCount > 0) {
      const msg = errors.length === 0
        ? `Predat la ${okCount} angajat${okCount === 1 ? "" : "i"} cu succes`
        : `Predat la ${okCount} angajat${okCount === 1 ? "" : "i"} din ${valid.length}`;
      toast.success(msg);
      onDone && onDone();
    }
    if (errors.length) {
      toast.error(`Eșuat la ${errors.length}: ${errors.slice(0, 3).join("; ")}${errors.length > 3 ? "…" : ""}`);
    } else {
      onClose && onClose();
    }
  };

  if (!item) return null;

  return (
    <Dialog open={!!open} onOpenChange={(v) => { if (!v && !busy) onClose && onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="bulk-assign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users size={18} className="text-cyan-600"/>
            Predă la mai mulți angajați
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Distribuie cantitatea disponibilă din stoc către mai mulți angajați într-un singur pas.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Info despre item-ul sursă */}
          <div className="rounded-md bg-cyan-50 border border-cyan-200 p-2.5 text-sm">
            <div className="font-semibold text-zinc-900">{item.name}</div>
            <div className="flex flex-wrap gap-2 mt-1 text-xs">
              {item.psa_size && (
                <span className="px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-900 font-bold">Mărime {item.psa_size}</span>
              )}
              <span className="px-1.5 py-0.5 rounded bg-white border border-cyan-300 text-cyan-900 font-bold">
                Stoc: {totalStock} buc.
              </span>
              {item.supplier && (
                <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-200">{item.supplier}</span>
              )}
            </div>
          </div>

          {/* Liste de angajați + cantități */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-zinc-600">Angajați destinatari</Label>
            {rows.map((row, idx) => {
              const availablePersons = persons.filter((p) => p.id === row.person_id || !usedIds.has(p.id));
              return (
                <div key={row._uid} className="flex gap-2 items-end" data-testid={`bulk-assign-row-${idx}`}>
                  <div className="flex-1">
                    <Select
                      value={row.person_id || ""}
                      onValueChange={(v) => updateRow(idx, { person_id: v })}
                    >
                      <SelectTrigger data-testid={`bulk-assign-person-${idx}`}>
                        <SelectValue placeholder="Selectează angajat" />
                      </SelectTrigger>
                      <SelectContent>
                        {availablePersons.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.nume} {p.prenume}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-24">
                    <Input
                      type="number"
                      min={1}
                      step={1}
                      value={row.quantity}
                      onChange={(e) => updateRow(idx, { quantity: e.target.value })}
                      data-testid={`bulk-assign-qty-${idx}`}
                      placeholder="Buc."
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeRow(idx)}
                    disabled={rows.length === 1}
                    title="Șterge rândul"
                    data-testid={`bulk-assign-remove-${idx}`}
                  >
                    <Trash2 size={16} className="text-red-600"/>
                  </Button>
                </div>
              );
            })}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addRow}
              className="w-full"
              data-testid="bulk-assign-add-row"
            >
              <Plus size={14} className="mr-1"/> Adaugă alt angajat
            </Button>
          </div>

          {/* Sumar */}
          <div className={`rounded-md border p-2 text-xs flex justify-between ${
            remaining < 0 ? "bg-red-50 border-red-200 text-red-900" :
            remaining === 0 ? "bg-emerald-50 border-emerald-200 text-emerald-900" :
            "bg-amber-50 border-amber-200 text-amber-900"
          }`} data-testid="bulk-assign-summary">
            <span>
              <strong>Total cerut:</strong> {totalRequested} buc.
            </span>
            <span>
              <strong>Rămâne în stoc:</strong> {remaining} buc.
            </span>
          </div>

          {/* Data predării — obligatorie */}
          <div>
            <Label className="text-xs flex items-center gap-1">📅 Data predării <span className="text-red-600">*</span></Label>
            <Input
              type="date"
              value={operationDate}
              onChange={(e) => setOperationDate(e.target.value)}
              max={todayIso}
              required
              className="mt-1"
              data-testid="bulk-assign-operation-date"
            />
            <div className="text-[10px] text-zinc-500 mt-1">
              Default azi. Modifică pentru a înregistra retroactiv o predare făcută într-o altă zi.
            </div>
          </div>

          {/* Notă opțională */}
          <div>
            <Label className="text-xs">Notă (opțional)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="ex: predare echipament iarnă 2026"
              data-testid="bulk-assign-note"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" type="button" onClick={onClose} disabled={busy}>Anulează</Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={busy || remaining < 0 || totalRequested <= 0 || !operationDate}
            data-testid="bulk-assign-save"
          >
            {busy ? "Se predă..." : `💾 Predă (${totalRequested} buc.)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
